/**
 * One row in the feed — shared by the bell dropdown and the full page.
 *
 * Deliberately ONE component for both surfaces. The dropdown and the page show
 * the same information at different widths, and the moment they are two
 * components they start to disagree about what a notification looks like —
 * which is how a "read" dot ends up rendering in one place and not the other.
 */
import { AlertTriangle, Bell, FileText, HardDrive, Home, Info, UserPlus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Link from "@/compat/link";

import { cn } from "@/lib/utils";
import type { NotificationItem as Item, NotificationKind, NotificationSeverity } from "./api";

const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  RENT_DUE: Home,
  RENT_REMINDER: Bell,
  REPORT: FileText,
  BACKUP: HardDrive,
  EXPIRY: AlertTriangle,
  ACCESS_REQUEST: UserPlus,
  FAMILY_ACTIVITY: Info,
};

/**
 * State is encoded in FORM (an inset bar) as well as colour — the `cp-severity-*`
 * discipline the design system already applies to tables, so severity survives
 * greyscale and colour-blindness.
 */
const SEVERITY_BAR: Record<NotificationSeverity, string> = {
  CRITICAL: "cp-severity-breach",
  WARNING: "cp-severity-duesoon",
  SUCCESS: "cp-severity-ok",
  INFO: "",
};

const SEVERITY_ICON_TONE: Record<NotificationSeverity, string> = {
  CRITICAL: "text-[var(--cp-rose-ink)]",
  WARNING: "text-[var(--cp-amber-ink)]",
  SUCCESS: "text-[var(--cp-emerald)]",
  INFO: "text-[var(--cp-ink-3)]",
};

/** `2026-08-21T09:12:00Z` -> `4h ago`. Past-focused: a feed looks backwards. */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";

  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  // Clock skew, or an expiry dated in the future — say something truthful
  // rather than "-3h ago".
  if (seconds < 60) return seconds < -60 ? "upcoming" : "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

interface Props {
  item: Item;
  onMarkRead: (id: string) => void;
  /** The dropdown is tighter than the page. */
  compact?: boolean;
}

export function NotificationRow({ item, onMarkRead, compact = false }: Props) {
  const Icon = KIND_ICON[item.kind] ?? Info;

  const inner = (
    <>
      <span
        className={cn(
          "mt-0.5 flex shrink-0 items-center justify-center",
          SEVERITY_ICON_TONE[item.severity],
        )}
        aria-hidden="true"
      >
        <Icon className={compact ? "h-4 w-4" : "h-[18px] w-[18px]"} />
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-[var(--cp-ink)]",
            compact ? "text-[13px]" : "text-sm",
            item.read ? "font-medium" : "font-semibold",
          )}
        >
          {item.title}
        </span>
        {item.body && (
          <span
            className={cn(
              "mt-0.5 block text-[var(--cp-ink-2)]",
              compact ? "truncate text-[11.5px]" : "text-xs",
            )}
          >
            {item.body}
          </span>
        )}
        <span className="mt-0.5 block text-[11px] text-[var(--cp-ink-3)]">
          {timeAgo(item.createdAt)}
        </span>
      </span>

      {/* An unread dot, not a "read" badge: the exception is what deserves the
          ink, and it doubles as the click target's affordance. */}
      {!item.read && (
        <span
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--cp-accent)]"
          aria-label="Unread"
        />
      )}
    </>
  );

  const shared = cn(
    "flex w-full items-start gap-2.5 text-left transition-colors",
    compact ? "px-3 py-2.5" : "px-3 py-3",
    SEVERITY_BAR[item.severity],
    item.read ? "opacity-70" : "",
    "hover:bg-[var(--cp-surface-2)]",
  );

  // Marking read on activation is the whole interaction: opening the thing a
  // notification points at IS reading it, so there is no separate "mark read"
  // control to hunt for. An item with no link still needs to be dismissible,
  // hence the button fallback rather than a non-interactive div.
  const handle = () => {
    if (!item.read) onMarkRead(item.id);
  };

  if (item.link) {
    return (
      <Link href={item.link} className={shared} onClick={handle}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" className={shared} onClick={handle}>
      {inner}
    </button>
  );
}
